<template>
  <div v-if="showData" class="erc__vehicle-remap-info" id="erc-vehicle-remap-info" ref="remapInfoContainer">
    <!-- Vehicle Image and Basic Info Section -->
    <div class="section-image-info">
         <!-- MODIFIED: Conditional rendering based on setting and image existence -->
         <div class="vehicle-image" ref="vehicleImageRef" v-if="showPhotosSetting">
          <img v-if="remapInfo.generation && remapInfo.generation.image" :src="getFullAssetUrl(remapInfo.generation.image)" alt="Vehicle Image">
            <!-- ADDED: Fallback Image -->
            <img v-else :src="fallbackImageUrl" alt="Vehicle Image Placeholder">
        </div>
        <!-- END MODIFIED -->
        <div class="logo-and-basic-info">
            <div class="brand-logo" ref="brandLogoRef">
              <img v-if="remapInfo.brand && remapInfo.brand.logo" :src="getFullAssetUrl(remapInfo.brand.logo)" alt="Brand Logo">
            </div>
            <div class="basic-info" ref="basicInfoRef">
                <div class="brand-name" ref="brandNameRef">{{ remapInfo.brand ? remapInfo.brand.name : '' }}</div>
                <div class="model-name" ref="modelNameRef">{{ remapInfo.model ? remapInfo.model.name : '' }}</div>
                <div v-if="remapInfo.generation" ref="generationInfoRef">
                    <div class="generation-name">{{ remapInfo.generation.name }}</div>
                    <div class="generation-year"> ({{ remapInfo.generation.yearStart }} - {{ remapInfo.generation.yearEnd || 'Present' }})</div>
                </div>
                <div class="engine-name" v-if="remapInfo.engine" ref="engineNameRef">{{ remapInfo.engine.name }}</div>
            </div>
        </div>
    </div>

    <!-- Data Section (Remaps and Engine Info) -->
    <div class="erc-row">
      <section class="remaps_engine__data">
        <!-- Remaps Data Grid -->
        <div class="remaps-data" ref="remapsDataRef">
          <h2 class="section-title section-title__remaps">Available Remaps</h2>
          <div class="available_remaps" v-if="remapInfo.engine" ref="remapsGridRef">
            <!-- OEM Column -->
            <div class="column column__oem">
              <div class="column-data">
                <div class="heading">
                  <h5 class="heading heading__oem">OEM</h5>
                </div>
                <div class="data">
                  <div class="data-box data-box__hp">
                    <span class="label">HP</span>
                    <span class="value">{{ Math.round(animatedValues.oem.hp) }}</span>
                  </div>
                  <div class="data-box data-box__nm">
                    <span class="label">Nm</span>
                    <span class="value">{{ Math.round(animatedValues.oem.nm) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Stage Columns (HP/Nm) -->
            <template v-for="stage in remapInfo.engine.stages" :key="'stage'+stage.stageNo">
              <div :class="'column column__stage column__stage_' + stage.stageNo">
                <div class="column-data">
                  <div class="heading">
                    <h5 :class="'heading heading__stage_' + stage.stageNo">
                      ST{{ stage.stageNo }}
                    </h5>
                  </div>
                  <div class="data">
                    <div class="data-box data-box__hp">
                      <span class="label">HP</span>
                      <span class="value">{{ Math.round(animatedValues.stages[stage.stageNo]?.hp || 0) }}</span>
                    </div>
                    <div class="data-box data-box__nm">
                      <span class="label">Nm</span>
                      <span class="value">{{ Math.round(animatedValues.stages[stage.stageNo]?.nm || 0) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <!-- **** Modifications Dropdown Section **** -->
            <div v-if="hasModifications" class="column column__modifications">
              <Dropdown
                :triggers="['hover']"
                :placement="'top'"
                :distance="6"
                theme="info-tooltip"
                class="info-tooltip__popper"
              >
                <button class="info-tooltip__reference">
                  <span>i</span>
                </button>
                <template #popper>
                  <div class="info-tooltip__text">
                    <h6>Modifications required</h6>
                    <!-- Loop through stages again to display modifications -->
                    <template v-for="stage in remapInfo.engine.stages" :key="'mod'+stage.stageNo">
                      <p v-if="stage.modifications">
                        <strong>ST{{ stage.stageNo }}:</strong> {{ stage.modifications }}
                      </p>
                    </template>
                  </div>
                </template>
              </Dropdown>
            </div>
            <!-- **** END Modifications Dropdown Section **** -->

            <!-- Gain Columns (Donuts) -->
            <template v-for="stage in remapInfo.engine.stages" :key="'stage-graph-'+stage.stageNo">
              <div :class="'column column__stage column__stage--gain column__stage_' + stage.stageNo">
                <div class="column-data">
                  <div class="heading">
                    <h5 :class="'heading heading__stage_' + stage.stageNo">
                      Gain ST{{ stage.stageNo }}
                    </h5>
                  </div>
                  <div class="data">
                    <!-- Power Donut -->
                    <div class="data-box data-box__graph">
                      <vc-donut
                          :background="donutTrackColor" 
                          :foreground="donutForegroundColor" 
                          :size="100" unit="px" :thickness="45"
                          :start-angle="180"
                          :sections="getStaticDonutSections(stage.stageNo, 'power')"
                      >
                        <template v-if="showAbsoluteGainInDonut">
                          <span class="absolute-gain-value">
                            +{{ getAbsoluteGain(stage.stageNo, 'power') }}
                          </span>
                          <span class="absolute-gain-unit">hp</span>
                        </template>
                        <template v-else>
                          <span class="percent-value">
                           +{{ Math.round(animatedValues.stages[stage.stageNo]?.powerPercent || 0) }}
                          </span>
                          <span class="percent-sign">%</span>
                        </template>
                      </vc-donut>
                    </div>
                    <!-- Torque Donut -->
                    <div class="data-box data-box__graph">
                      <vc-donut
                          :background="donutTrackColor"
                          :foreground="donutForegroundColor" 
                          :size="100" unit="px" :thickness="45"
                          :start-angle="180"
                          :sections="getStaticDonutSections(stage.stageNo, 'torque')"
                      >
                        <template v-if="showAbsoluteGainInDonut">
                           <span class="absolute-gain-value">
                            +{{ getAbsoluteGain(stage.stageNo, 'torque') }}
                          </span>
                          <span class="absolute-gain-unit">Nm</span>
                        </template>
                        <template v-else>
                          <span class="percent-value">
                           +{{ Math.round(animatedValues.stages[stage.stageNo]?.torquePercent || 0) }}
                          </span>
                          <span class="percent-sign">%</span>
                        </template>
                      </vc-donut>
                    </div>
                  </div>
                </div>
              </div>
            </template>

          </div> <!-- End available_remaps -->

          <!-- Available Options Section -->
          <div class="available-options" v-if="remapInfo.engine && Array.isArray(remapInfo.engine.options) && remapInfo.engine.options.length > 0" ref="availableOptionsRef">
            <h6 class="options-title">Available Options</h6>
            <p class="options-list">
              {{ remapInfo.engine.options.map(option => option.name).join(', ') }}
            </p>
          </div>
          <!-- End Available Options Section -->

        </div> <!-- End remaps-data -->

        <!-- Engine Info Details -->
        <div class="remaps-info" v-if="remapInfo.engine" ref="engineDetailsRef">
          <h2 class="section-title section-title__engine column">Engine Info</h2>
          <!-- Restructured Engine Info -->
          <div class="engine-info-restructured">
            <p class="engine-detail-row" v-if="remapInfo.engine.engineCode">
              {{ remapInfo.engine.engineCode }}
            </p>
            <p class="engine-detail-row" v-if="remapInfo.engine.cylinders || remapInfo.engine.capacity">
              <span v-if="remapInfo.engine.cylinders">{{ remapInfo.engine.cylinders }} cyl.</span>
              <span v-if="remapInfo.engine.cylinders && remapInfo.engine.capacity"> / </span>
              <span v-if="remapInfo.engine.capacity">{{ remapInfo.engine.capacity }} ccm</span>
            </p>
            <p class="engine-detail-row" v-if="remapInfo.engine.engineType || remapInfo.engine.fuelType">
              <span v-if="remapInfo.engine.engineType">{{ remapInfo.engine.engineType }}</span>
              <span v-if="remapInfo.engine.engineType && remapInfo.engine.fuelType">&nbsp;</span>
              <span v-if="remapInfo.engine.fuelType">{{ remapInfo.engine.fuelType }}</span>
            </p>
            <template v-if="remapInfo.engine.ecus && remapInfo.engine.ecus.length > 0 && remapInfo.engine.ecus[0].name">
              <p class="engine-detail-label">ECU:</p>
              <p class="engine-detail-row">{{ remapInfo.engine.ecus[0].name }}</p>
            </template>
          </div>
          <!-- End Restructured Engine Info -->

          <!-- Tools Section -->
           <div class="tools" v-if="Array.isArray(remapInfo.engine.tools) && remapInfo.engine.tools.length > 0">
            <h5>Tools</h5>
            <span class="tools-list">{{ remapInfo.engine.tools.map(tool => tool.name).join(', ') }}</span>
          </div>

          <!-- Methods Section -->
          <div class="methods" v-if="Array.isArray(remapInfo.engine.methods) && remapInfo.engine.methods.length > 0">
            <h5>Methods</h5>
            <div class="methods-icon-container">
              <template v-for="method in remapInfo.engine.methods">
                <img
                  v-if="getMethodIconPath(method.name)"
                  :key="method.id"
                  :src="getMethodIconPath(method.name)"
                  :alt="method.name"
                  class="method-icon"
                />
              </template>
            </div>
          </div>
          <!-- End Methods Section -->
        </div> <!-- End remaps-info -->
      </section> <!-- End remaps_engine__data -->
    </div> <!-- End erc-row -->

    <!-- Dynochart Section -->
    <section v-if="hasOemChartData" class="dynochart-section">
        <h2 class="section-title section-title__dynochart">Dynochart</h2>
        <div class="chart-container">
          <Chart :options="chartOptions" ref="dynoChart"></Chart>
        </div>
    </section>
    <!-- End Dynochart Section -->

    <!-- Optional: Error Message Display -->
    <div v-if="fetchError" class="error-message">{{ fetchError }}</div>

  </div> <!-- End erc__vehicle-remap-info -->
  <!-- Optional: Loading Indicator -->
  <!-- <div v-else-if="isLoading">Loading remap info...</div> -->
</template>


<script>
import { API_BASE } from "@/constants/api";
import { buildApiUrl } from '@/utils/apiUtils';
import { getFullAssetUrl } from '@/utils/urlUtils';
import { Dropdown } from 'floating-vue';
import { VcDonut } from 'vue-css-donut-chart';
import 'floating-vue/dist/style.css';
import 'vue-css-donut-chart/dist/vcdonut.css';
import { gsap } from 'gsap';

// --- Highcharts Imports ---
import { Chart } from 'highcharts-vue';
// Optional: Load Highcharts modules like accessibility if needed
// import AccessibilityModule from 'highcharts/modules/accessibility';
// AccessibilityModule(Highcharts);

export default {
  name: "RemapInfo",
  props: {
    vehicleData: {
      type: Object,
      required: true,
    }
  },
  components: {
    Dropdown,
    'vc-donut': VcDonut,
    Chart
  },
  data() {
    return {
      showData: false,
      remapInfo: {
        brand: null, model: null, generation: null, engine: null,
      },
      animatedValues: {
        oem: { hp: 0, nm: 0 },
        stages: { } // Populated dynamically
      },
      showAbsoluteGainInDonut: false, // Start by showing percentage
      donutIntervalId: null, // To store the interval timer ID
      fetchError: null, // For displaying user-facing errors
      // isLoading: false // Optional: for loading indicator
    }
  },

  computed: {
    // --- ADDED: Computed property for show photos setting ---
    showPhotosSetting() {
      // Access the setting passed from PHP, default to true if not found
      return window.ercData?.settings?.show_photos ?? true;
    },
            // --- MODIFIED: Computed property for fallback image URL based on vehicle type ---
    fallbackImageUrl() {
      // Default vehicle type ID to 1 (e.g., Car)
      let vehicleTypeId = 1;
      const defaultFilename = 'nophoto-1.webp'; // Keep the default explicit

      // --- CHANGE: Read 'vehicleType' from the prop instead of 'vehicle_type_id' ---
      const rawTypeId = this.vehicleData?.vehicleType;
      // --- END CHANGE ---

      // Ensure it's a valid positive number
      const typeIdFromData = parseInt(rawTypeId, 10);
      let filenameToUse = defaultFilename; // Start with the default

      if (!isNaN(typeIdFromData) && typeIdFromData > 0) {
        // If the ID from data is valid, use it to construct the filename
        vehicleTypeId = typeIdFromData;
        filenameToUse = `nophoto-${vehicleTypeId}.webp`;
        // Optional: uncomment for debugging
        // console.log(`RemapInfo: Using fallback image for vehicle type ${vehicleTypeId}: ${filenameToUse}`);
      } else {
        // Optional: Log a warning if the ID is missing or invalid
        // --- CHANGE: Update warning message ---
        console.warn(`RemapInfo: Missing or invalid 'vehicleType' in vehicleData prop. Defaulting to type 1. Data received:`, this.vehicleData);
        // --- END CHANGE ---
        // filenameToUse remains defaultFilename
      }

      // Determine base URL based on environment (same logic as before)
      let baseUrl = '/'; // Default for safety
      if (process.env.NODE_ENV === 'development') {
        // In local dev, assets from 'public' are served at the root path
        // Assuming your images are in 'public/img/'
        baseUrl = '/img/';
      } else {
        // In production (WordPress plugin), use the plugin URL and dist path
        const pluginUrl = window.ercData?.plugin_url || '/'; // Get base URL from WP
        const pluginBaseUrl = pluginUrl.endsWith('/') ? pluginUrl : pluginUrl + '/';
        // This path should match where the build process copies the public assets
        baseUrl = `${pluginBaseUrl}dist/img/`;
      }

      // Construct the final URL
      return `${baseUrl}${filenameToUse}`;
    },
    // --- END MODIFIED ---


    hasModifications() {
      return !!this.remapInfo?.engine?.stages?.some(stage => !!stage.modifications);
    },
    hasOemChartData() {
      // Check if essential chart data exists and is a non-empty string
      const oemData = this.remapInfo?.engine?.oemChartData;
      return typeof oemData?.rpm_values === 'string' && oemData.rpm_values.length > 0 &&
             typeof oemData?.hp_values === 'string' && oemData.hp_values.length > 0 && // Can be direct or template %
             typeof oemData?.nm_values === 'string' && oemData.nm_values.length > 0;  // Can be direct or template %
    },
    // --- Corrected computed properties for donut colors ---
    donutTrackColor() {
      // Check if $el exists, is connected, and closest is a function
      if (this.$el && this.$el.isConnected && typeof this.$el.closest === 'function') {
        const container = this.$el.closest('.erc-vehicle-catalog-container');
        // Also check if container was found and getComputedStyle exists
        if (container && typeof getComputedStyle === 'function') {
          // Read the CSS variable from the container
          return getComputedStyle(container).getPropertyValue('--erc-donut-track-color').trim() || '#ffffff'; // Fallback 1
        }
      }
      // Fallback 2: If checks fail (e.g., before mounted)
      return '#ffffff';
    },
    donutForegroundColor() {
      // Check if $el exists, is connected, and closest is a function
      if (this.$el && this.$el.isConnected && typeof this.$el.closest === 'function') {
        const container = this.$el.closest('.erc-vehicle-catalog-container');
        // Also check if container was found and getComputedStyle exists
        if (container && typeof getComputedStyle === 'function') {
          // Read the CSS variable from the container
          return getComputedStyle(container).getPropertyValue('--erc-donut-foreground-color').trim() || '#f0f0f0'; // Fallback 1
        }
      }
      // Fallback 2: If checks fail (e.g., before mounted)
      return '#f0f0f0';
    },
    // --- END Corrected ---
    chartOptions() {
      // --- Robust Initial Check ---
      if (!this.hasOemChartData) {
          console.warn("chartOptions: Missing or invalid oemChartData structure.");
          return {};
      }
      // --- END ROBUST CHECK ---

      const engine = this.remapInfo.engine;
      const oemChartDataSource = engine.oemChartData;
      // Check if the source indicates it was derived from a template
      // Local API uses "template", Live API uses "template-ID"
      const oemSourceIsTemplate = typeof oemChartDataSource.source === 'string' &&
                                 (oemChartDataSource.source === 'template' || oemChartDataSource.source.startsWith('template-'));
      const basePower = parseInt(engine.power, 10); // Base OEM power
      const baseTorque = parseInt(engine.torque, 10); // Base OEM torque

      // --- Parse RPM Data ---
      const rpmValues = this.parseCsvString(oemChartDataSource.rpm_values);
      if (!Array.isArray(rpmValues) || rpmValues.length === 0) {
          console.error("chartOptions: Failed to parse RPM data or RPM data is empty.");
          return { title: { text: 'Error: Invalid RPM data' } };
      }

      // --- Determine and Parse OEM HP/NM Data ---
      let oemHpData = [];
      let oemNmData = [];
      let templateHpPercentages = null; // Store template % if source is template
      let templateNmPercentages = null;

      // Parse the main values provided by the API
      const oemHpValuesParsed = this.parseCsvString(oemChartDataSource.hp_values);
      const oemNmValuesParsed = this.parseCsvString(oemChartDataSource.nm_values);

      // Validate parsed values against RPM length
      if (!Array.isArray(oemHpValuesParsed) || oemHpValuesParsed.length !== rpmValues.length ||
          !Array.isArray(oemNmValuesParsed) || oemNmValuesParsed.length !== rpmValues.length) {
          console.warn(`Highcharts OEM data mismatch: Lengths - RPM: ${rpmValues.length}, HP: ${oemHpValuesParsed?.length}, NM: ${oemNmValuesParsed?.length}`);
          return { title: { text: 'Error: OEM Data length issue' } };
      }

      if (oemSourceIsTemplate) {
          // --- Extract Template ID (if available) ---
          const templateId = oemChartDataSource.source.includes('-') ? (oemChartDataSource.source.split('-')[1] || 'Unknown') : 'N/A';
          // --- CORRECTED Log with Template ID ---
          console.log(`OEM Source is Template ID ${templateId}. Calculating absolute values from percentages.`); // Use backticks ` `

          // Values ARE percentages, store them for stage fallback
          templateHpPercentages = oemHpValuesParsed;
          templateNmPercentages = oemNmValuesParsed;

          // Validate base power/torque needed for calculation
          if (isNaN(basePower) || isNaN(baseTorque)) {
              console.warn("Highcharts template calculation error: Base power or torque is invalid.");
              return { title: { text: 'Error: Invalid base power/torque for template calculation' } };
          }

          // Calculate absolute values for OEM series
          oemHpData = templateHpPercentages.map(val => Math.round((val / 100) * basePower));
          oemNmData = templateNmPercentages.map(val => Math.round((val / 100) * baseTorque));

      } else { // Source is 'engine' (direct) or unknown (treat as direct)
          console.log("OEM Source is Direct. Using absolute values.");
          oemHpData = oemHpValuesParsed; // Use direct absolute values
          oemNmData = oemNmValuesParsed; // Use direct absolute values
      }
      // --- End OEM Data Determination ---


      // --- Initialize Series Array ---
      const series = [
          { name: 'OEM Torque', type: 'spline', yAxis: 1, data: oemNmData, color: 'var(--erc-color-oem)', dashStyle: 'Dot', tooltip: { valueSuffix: ' Nm' } },
          { name: 'OEM Power', type: 'spline', yAxis: 0, data: oemHpData, color: 'var(--erc-color-oem)', tooltip: { valueSuffix: ' hp' } }
      ];

      // --- Process Stage Data ---
      if (engine.stages && Array.isArray(engine.stages)) {
        engine.stages.forEach(stage => {
          const stagePower = parseInt(stage.power, 10);
          const stageTorque = parseInt(stage.torque, 10);
          if (isNaN(stagePower) || isNaN(stageTorque)) {
            console.warn(`Stage ${stage.stageNo}: SKIPPING chart - Invalid stage peak power/torque.`);
            return; // Skip this stage if peak values are invalid
          }

          let stageHpData = [];
          let stageNmData = [];
          let calculationSource = 'none'; // Track how data was derived

          // Try parsing direct stage values first
          const parsedStageHp = this.parseCsvString(stage.hp_values);
          const parsedStageNm = this.parseCsvString(stage.nm_values);

          // Check if direct stage values are valid and match RPM length
          if (Array.isArray(parsedStageHp) && parsedStageHp.length === rpmValues.length &&
              Array.isArray(parsedStageNm) && parsedStageNm.length === rpmValues.length)
          {
              console.log(`Stage ${stage.stageNo}: Using DIRECT stage chart data.`);
              stageHpData = parsedStageHp; // Use direct absolute values
              stageNmData = parsedStageNm; // Use direct absolute values
              calculationSource = 'direct';
          }
          // Else, if direct stage values are invalid/missing AND OEM source was a template
          else if (oemSourceIsTemplate)
          {
              console.log(`Stage ${stage.stageNo}: Attempting TEMPLATE fallback for stage chart data.`);
              // Use the template percentages we stored earlier from the OEM calculation
              // --- CRITICAL CHECK ---
              // Ensure the stored percentages are valid arrays and match RPM length
              if (!Array.isArray(templateHpPercentages) || templateHpPercentages.length !== rpmValues.length ||
                  !Array.isArray(templateNmPercentages) || templateNmPercentages.length !== rpmValues.length) {
                  console.warn(`Stage ${stage.stageNo}: SKIPPING chart - Invalid or missing TEMPLATE percentage data for fallback calculation. Check API response for oemChartData.hp_values/nm_values when source is template.`);
                  return; // Skip this stage if template data is bad
              }
              // --- END CRITICAL CHECK ---

              // Calculate absolute values using stage's peak power/torque and TEMPLATE percentages
              stageHpData = templateHpPercentages.map(val => Math.round((val / 100) * stagePower));
              stageNmData = templateNmPercentages.map(val => Math.round((val / 100) * stageTorque));
              console.log(`Stage ${stage.stageNo}: Successfully calculated stage data from template.`);
              calculationSource = 'template';
          }
          // Else, no valid direct data and no template fallback possible
          else {
              console.warn(`Stage ${stage.stageNo}: SKIPPING chart - No valid direct stage data and OEM source was not template.`);
              return; // Skip this stage
          }

          // Add stage series to the chart only if data was successfully processed
          if (calculationSource !== 'none') {
              let stageColorVar = '--erc-color-st1';
              if (stage.stageNo === 2) stageColorVar = '--erc-color-st2';
              // Add more colors for ST3, ST4 etc. if needed

              series.push({ name: `ST${stage.stageNo} Torque`, type: 'spline', yAxis: 1, data: stageNmData, color: `var(${stageColorVar})`, dashStyle: 'Dot', tooltip: { valueSuffix: ' Nm' } });
              series.push({ name: `ST${stage.stageNo} Power`, type: 'spline', yAxis: 0, data: stageHpData, color: `var(${stageColorVar})`, tooltip: { valueSuffix: ' hp' } });
          }

        }); // End forEach stage
      }
      // --- End Stage Data Processing ---

      // --- Return Final Highcharts Configuration ---
      return {
        chart: {
            type: 'spline',
            zoomType: 'x'
            // Optional: Style mode for dark theme compatibility
            // styleMode: (window.ercData?.settings?.theme === 'dark') // Needs testing
            // Or set background/text colors explicitly based on theme
            // backgroundColor: (window.ercData?.settings?.theme === 'dark') ? '#2c2c2c' : '#ffffff',
        },
        title: { text: null },
        credits: { enabled: false },
        xAxis: {
            categories: rpmValues,
            title: { text: 'RPM' },
            // Optional: Theme adjustments
            // lineColor: 'var(--erc-border-color)',
            // tickColor: 'var(--erc-border-color)',
            // labels: { style: { color: 'var(--erc-text-color)' } },
            // title: { style: { color: 'var(--erc-text-color)' } },
            crosshair: {
              width: 1, // Line width
              color: '#cccccc', // Use a theme variable or a specific color like 'gray' or '#cccccc'
              dashStyle: 'SSolid', // Style of the line (Solid, Dot, Dash, ShortDash etc.)
              snap: true // Set to true if you want the line to snap to the nearest point/category
            }
        },
        yAxis: [
          { // Power Axis
            labels: { format: '{value}' /*, style: { color: 'var(--erc-text-color)' } */ },
            title: { text: 'Power (hp)' /*, style: { color: 'var(--erc-text-color)' } */ },
            opposite: false, tickAmount: 9, startOnTick: true, endOnTick: true,
            // gridLineColor: 'var(--erc-border-color)',
            // lineColor: 'var(--erc-border-color)',
            // tickColor: 'var(--erc-border-color)',
            crosshair: {
              width: 1, // Line width
              color: '#cccccc', // Use a theme variable or a specific color like 'gray' or '#cccccc'
              dashStyle: 'SSolid', // Style of the line (Solid, Dot, Dash, ShortDash etc.)
              snap: true // Set to true if you want the line to snap to the nearest point/category
            }
          },
          { // Torque Axis
            labels: { format: '{value}' /*, style: { color: 'var(--erc-text-color)' } */ },
            title: { text: 'Torque (Nm)' /*, style: { color: 'var(--erc-text-color)' } */ },
            opposite: true, tickAmount: 9, startOnTick: true, endOnTick: true,
            // gridLineColor: 'var(--erc-border-color)',
            // lineColor: 'var(--erc-border-color)',
            // tickColor: 'var(--erc-border-color)',
          }
        ],
        tooltip: {
          shared: true,
          // Optional: Style tooltip based on theme
          // backgroundColor: 'rgba(0, 0, 0, 0.75)', // Example dark tooltip
          // style: { color: '#f0f0f0' },
          formatter: function () {
            const categoryName = this.points?.[0]?.key ?? this.x ?? '';
            let tooltip = `<b>${categoryName} rpm</b><br/>`;
            const pointsArray = Array.isArray(this.points) ? this.points : [];
            const sortedPoints = pointsArray.sort((a, b) => {
              const nameA = a.series.name.toUpperCase();
              const nameB = b.series.name.toUpperCase();
              if (nameA.startsWith('OEM') && !nameB.startsWith('OEM')) return -1;
              if (!nameA.startsWith('OEM') && nameB.startsWith('OEM')) return 1;
              return nameA.localeCompare(nameB);
            });
            sortedPoints.forEach(point => {
              const unit = point.series.name.toLowerCase().includes('power') ? 'hp' : 'Nm';
              tooltip += `<span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${point.y} ${unit}</b><br/>`;
            });
            return tooltip;
          }
        },
        legend: {
            layout: 'horizontal', align: 'center', verticalAlign: 'bottom',
            // Optional: Theme adjustments
            // itemStyle: { color: 'var(--erc-text-color)' },
            // itemHoverStyle: { color: 'var(--erc-text-heading)' }
        },
        plotOptions: {
          spline: {
            lineWidth: 2.5, // Increase default line thickness (e.g., from default 2 to 3)
            marker: {
              enabled: true,
              radius: 4.5, // Increase default marker radius (e.g., from default 4 to 5)
              symbol: 'circle',
              states: { // Define hover states for markers
                hover: {
                  enabled: true,
                  radius: 6, // Make hover radius proportionally larger (e.g., 5 -> 7)
                  lineWidthPlus: 0.5 // Optional: Add border width on hover if desired
                }
              }
            },
            states: { // Define hover states for the spline line itself
              hover: {
                lineWidthPlus: 1 // Increase line width on hover (e.g., adds 2 to the base lineWidth of 3)
              }
            }
          }
        },
        accessibility: { enabled: false },
        series: series,
        responsive: {
          rules: [{
            // --- Rule for smaller screens (e.g., mobile) ---
            condition: {
              maxWidth: 767 // Apply these rules when chart width is 767px or less
            },
            chartOptions: {
              legend: {
                enabled: false // Hide the legend completely
              },
              yAxis: [
                { // Power Axis
                  title: { text: null }, // Hide Y-axis title
                  tickAmount: 5 // Reduce number of ticks/labels
                },
                { // Torque Axis
                  title: { text: null }, // Hide Y-axis title
                  tickAmount: 5 // Reduce number of ticks/labels
                }
              ],
              xAxis: {
                title: { text: null } // Hide X-axis title
              },
              // Optional: Simplify tooltip further if needed
              tooltip: { shared: false } // Example: Show only hovered point
            }
            // --- End Rule ---
          }]
        } // End responsive
      };
    }
  }, // End computed


  watch: {
    vehicleData: {
        handler(newVal) {

          // <<< LOG 1: Is the watcher firing at all? >>>
            console.log("[LIVE_DEBUG_FLOW] vehicleData watcher FIRED. newVal:", JSON.parse(JSON.stringify(newVal)));

            // Declare apiUrl higher up
            let apiUrl = '';

            if (!newVal || !newVal.brand || !newVal.model || !newVal.engine) {
                this.resetComponentState();
                return;
            }
            this.showData = false;
            this.remapInfo = { brand: null, model: null, generation: null, engine: null };
            this.resetAnimatedValues();
            this.fetchError = null;

            try { // Added try block for safety around URL building
                const params = new URLSearchParams(newVal);
                // Assign apiUrl value
                apiUrl = buildApiUrl('vdb/remap-info', params);

                fetch(apiUrl, { headers: { 'Content-Type': 'application/json' } })
                .then(response => {
                    if (!response.ok) {
                        return response.text().then(text => {
                            console.error(`HTTP error! Status: ${response.status} for URL: ${apiUrl}`, text);
                            throw new Error(`HTTP error! status: ${response.status}`);
                        });
                    }
                    return response.json();
                 })
                .then(data => {
                    if (data?.error) {
                        console.error("API Error:", data.error, "Request Params:", params.toString());
                        this.fetchError = data.error || "An error occurred while fetching remap data.";
                        this.resetComponentState();
                    }
                    else if (data?.brand && data?.model && data?.generation && data?.engine) {
                        this.remapInfo = data;
                        this.showData = true;
                        this.$emit('remapDataFetched', data); // Keep if needed elsewhere

                        this.prepareAnimatedValuesStructure();
                        // <<< LOG 2: What's in animatedValues.stages after prep? >>>
                        console.log("[LIVE_DEBUG_DATA] After prepareAnimatedValuesStructure, animatedValues.stages:", JSON.parse(JSON.stringify(this.animatedValues.stages)));
                        console.log("[LIVE_DEBUG_DATA] remapInfo.engine.stages used for prep:", JSON.parse(JSON.stringify(this.remapInfo.engine.stages)));

                        this.$nextTick(() => {
                            // --- GSAP Animation ---
                            const imageContainer = this.$refs.vehicleImageRef;
                            const logo = this.$refs.brandLogoRef;
                            const basicInfo = this.$refs.basicInfoRef;
                            const remapsData = this.$refs.remapsDataRef;
                            const engineDetails = this.$refs.engineDetailsRef; // Section with engine info, tools, methods
                            const options = this.$refs.availableOptionsRef; // Optional section
                            const chartComponent = this.$refs.dynoChart;
                            const chartSection = chartComponent?.$el?.closest('.dynochart-section'); // Dynochart section

                          // <<< LOG 3: Are the core refs available before the main animation block? >>>
                            console.log("[LIVE_DEBUG_REFS] Refs check before GSAP block:", {
                                imageContainer: !!imageContainer,
                                logo: !!logo,
                                basicInfo: !!basicInfo,
                                remapsData: !!remapsData,
                                engineDetails: !!engineDetails
                            });

                            // UPDATED: Removed 'titles' from the condition
                            if (imageContainer && logo && basicInfo && remapsData && engineDetails) {
                                const tl = gsap.timeline({ defaults: { duration: 0.6, ease: 'power2.out' } });

                                // Filter out potentially null refs (like options, chartSection)
                                const elementsToAnimate = [imageContainer, logo, basicInfo, remapsData, engineDetails, options, chartSection].filter(el => el);

                                // <<< LOG 4: Inside the main animation block, before stage loop >>>
                                console.log("[LIVE_DEBUG_FLOW] Core refs OK. Entering GSAP animation block.");
                                console.log("[LIVE_DEBUG_DATA] animatedValues.stages before loop:", JSON.parse(JSON.stringify(this.animatedValues.stages)));
                                console.log("[LIVE_DEBUG_DATA] Keys of animatedValues.stages:", Object.keys(this.animatedValues.stages));

                                // Initial states
                                gsap.set(elementsToAnimate, { autoAlpha: 0 });
                                gsap.set(imageContainer, { x: -100 });
                                gsap.set([logo, basicInfo], { x: 50 });
                                if (chartSection) gsap.set(chartSection, { y: 50 });

                                // Animation sequence
                                tl.to(imageContainer, { autoAlpha: 1, x: 0 }, "+=0.1")
                                  .to([logo, basicInfo], { autoAlpha: 1, x: 0 }, "-=0.3")
                                  // REMOVED: .to(titles, { autoAlpha: 1 }, "-=0.3")
                                  .to(remapsData, { autoAlpha: 1 }, "-=0.2");

                                // --- Number animations (SHOULD RUN NOW) ---
                                const counterDuration = 1.0;
                                const counterEase = 'none'; // Use 'none' for linear count-up
                                tl.to(this.animatedValues.oem, {
                                    hp: this.remapInfo.engine.power || 0,
                                    nm: this.remapInfo.engine.torque || 0,
                                    duration: counterDuration,
                                    ease: counterEase,
                                    snap: { hp: 1, nm: 1 } // Snap to whole numbers
                                }, "<0.1"); // Start slightly after grid appears

                                Object.keys(this.animatedValues.stages).forEach(stageNoKey => {
                                    const stageNo = parseInt(stageNoKey, 10);
                                    const targetStage = this.remapInfo.engine.stages.find(s => parseInt(s.stageNo, 10) === stageNo);
                                    if (targetStage) {
                                      // --- START LOGGING ---
                                        console.log(`[LIVE_DEBUG] Animating Stage ${stageNoKey}:
                                            Raw API Power: '${targetStage.power}' (Type: ${typeof targetStage.power}),
                                            Raw API Torque: '${targetStage.torque}' (Type: ${typeof targetStage.torque}),
                                            Target HP for GSAP: ${Number(targetStage.power) || 0},
                                            Target Nm for GSAP: ${Number(targetStage.torque) || 0},
                                            Animating object: this.animatedValues.stages[${stageNoKey}]
                                        `);
                                        // --- END LOGGING ---
                                        tl.to(this.animatedValues.stages[stageNo], {
                                            hp: Number(targetStage.power) || 0, // Ensure numeric value
                                            nm: Number(targetStage.torque) || 0, // Ensure numeric value
                                            powerPercent: this.getStaticPercent(stageNo, 'power'),
                                            torquePercent: this.getStaticPercent(stageNo, 'torque'),
                                            duration: counterDuration,
                                            ease: counterEase,
                                            snap: { hp: 1, nm: 1, powerPercent: 1, torquePercent: 1 } // Snap values
                                        }, "<"); // Run concurrently with OEM animation
                                        } else {
                                        console.warn(`[LIVE_DEBUG_WARN] GSAP Animation Loop: Could not find targetStage for stageNoKey ${stageNoKey}.`);
                                    }
                                });
                                // --- End Number animations ---

                                // Animate the rest of the content
                                tl.to(engineDetails, { autoAlpha: 1 }, "<0.2"); // Animate engine details visibility
                                if (options) { tl.to(options, { autoAlpha: 1 }, "<"); } // Animate options if present
                                if (chartSection) { tl.to(chartSection, { autoAlpha: 1, y: 0 }, "<0.1"); } // Animate chart if present

                                // Donut interval setup
                                tl.eventCallback("onComplete", () => {
                                    if (this.donutIntervalId) clearInterval(this.donutIntervalId);
                                    this.donutIntervalId = setInterval(() => { this.showAbsoluteGainInDonut = !this.showAbsoluteGainInDonut; }, 3000);
                                });
                            } else {
                                // Log which refs might be missing if the condition still fails
                               console.warn("[LIVE_DEBUG_WARN] GSAP Animation SKIPPED: One or more core refs (imageContainer, logo, basicInfo, remapsData, engineDetails) were not found.");
                                // Fallback animation
                                const container = this.$refs.remapInfoContainer;
                                if (container) gsap.from(container, { autoAlpha: 0, duration: 0.5, delay: 0.1 });
                            }
                            // --- END GSAP ---
                        });
                    } else {
                        console.error("API Error: Invalid data structure received.", data, "Request Params:", params.toString());
                        this.fetchError = "Received invalid data structure from the server.";
                        this.resetComponentState();
                    }
                })
                .catch(error => {
                    if (!this.fetchError) {
                       console.error("Fetch Error:", error, "URL:", apiUrl);
                       this.fetchError = "An unexpected error occurred while fetching remap details.";
                    }
                    this.resetComponentState();
                 });

            } catch (setupError) {
                console.error("Error preparing fetch request:", setupError);
                this.fetchError = "Failed to prepare request.";
                this.resetComponentState();
            }
        },
        // deep: true // Usually not needed
    }
  }, // End watch



  beforeUnmount() {
    if (this.donutIntervalId) {
      clearInterval(this.donutIntervalId);
      this.donutIntervalId = null;
    }
  },

  methods: {
    getFullAssetUrl,
    resetAnimatedValues() {
        this.animatedValues.oem = { hp: 0, nm: 0 };
        this.animatedValues.stages = {};
    },
    prepareAnimatedValuesStructure() {
        this.resetAnimatedValues();
        if (this.remapInfo?.engine?.stages && Array.isArray(this.remapInfo.engine.stages)) {
            this.remapInfo.engine.stages.forEach(stage => {
                const parsedStageNo = parseInt(stage?.stageNo, 10);
                if (stage && !isNaN(parsedStageNo) && parsedStageNo > 0) {
                    // Use the parsed number as the key for consistency
                    this.animatedValues.stages[parsedStageNo] = { hp: 0, nm: 0, powerPercent: 0, torquePercent: 0 };
                } else {
                    console.warn("prepareAnimatedValuesStructure: Invalid stage or stageNo found", stage);
                }
            });
        }
    },
    resetComponentState() {
        this.showData = false;
        this.remapInfo = { brand: null, model: null, generation: null, engine: null };
        this.resetAnimatedValues();
        if (this.donutIntervalId) {
            clearInterval(this.donutIntervalId);
            this.donutIntervalId = null;
        }
        this.showAbsoluteGainInDonut = false;
        this.fetchError = null; // Clear errors on reset
    },
    percentIncrease(increasedValue, lowerValue) {
      const low = parseInt(lowerValue, 10);
      const high = parseInt(increasedValue, 10);
      if (isNaN(low) || isNaN(high) || low <= 0 || high <= low) return 0;
      return Math.round(((high / low) - 1) * 100);
    },
    getStaticPercent(stageNo, type) {
      const stage = this.remapInfo?.engine?.stages?.find(s => parseInt(s.stageNo, 10) === stageNo);
      if (!stage || !this.remapInfo?.engine) return 0;
      const value = (type === 'power') ? stage.power : stage.torque;
      const baseValue = (type === 'power') ? this.remapInfo.engine.power : this.remapInfo.engine.torque;
      return this.percentIncrease(value, baseValue);
    },
    getAbsoluteGain(stageNo, type) {
      const stage = this.remapInfo?.engine?.stages?.find(s => s.stageNo === stageNo);
      if (!stage || !this.remapInfo?.engine) return 0;
      const stageValue = parseInt((type === 'power') ? stage.power : stage.torque, 10);
      const baseValue = parseInt((type === 'power') ? this.remapInfo.engine.power : this.remapInfo.engine.torque, 10);
      if (isNaN(stageValue) || isNaN(baseValue)) return 0;
      return Math.max(0, stageValue - baseValue);
    },
    getStaticDonutSections(stageNo, type) {
      const numericStageNo = parseInt(stageNo, 10);
      const animatedPercent = this.animatedValues.stages[numericStageNo]?.[type === 'power' ? 'powerPercent' : 'torquePercent'] || 0;
      let color = '#cccccc';
      const defaultSt1Color = window.ercData?.settings?.default_color_st1 || '#ff5419';
      const defaultSt2Color = window.ercData?.settings?.default_color_st2 || '#e82409';
      const rootElement = this.$el;

      if (stageNo === 1) {
          if (rootElement instanceof Element) { // Check if it's a valid element
              color = getComputedStyle(rootElement).getPropertyValue('--erc-color-st1').trim() || defaultSt1Color;
          } else {
              color = defaultSt1Color; // Fallback if element not ready
          }
      } else if (String(stageNo) === "2") { 
          if (rootElement instanceof Element) { // Check if it's a valid element
              color = getComputedStyle(rootElement).getPropertyValue('--erc-color-st2').trim() || defaultSt2Color;
          } else {
              color = defaultSt2Color; // Fallback if element not ready
          }
      }

      const clampedValue = Math.max(0, Math.min(Math.round(animatedPercent), 100));
      if (clampedValue <= 0) return [];
      return [{ value: clampedValue, color: color }];
    },
    getMethodIconPath(methodName) {
      if (!methodName || typeof methodName !== 'string') return null;
      const nameLower = methodName.toLowerCase();
      let iconFilename = null;

      // Determine the correct icon filename
      if (nameLower === 'obd') iconFilename = 'obd.svg';
      else if (nameLower === 'bench') iconFilename = 'bench.svg';
      else if (nameLower === 'boot') iconFilename = 'boot.svg';

      // If no matching icon, return null
      if (!iconFilename) return null;

      // --- START: Updated Base URL Logic ---
      let baseUrl = '/'; // Default for safety

      if (process.env.NODE_ENV === 'development') {
        // In local dev, assets from 'public' are served at the root path.
        // Assuming your method icons are in 'public/img/'
        baseUrl = '/img/';
      } else {
        // In production (WordPress plugin), use the plugin URL and dist path
        const pluginUrl = window.ercData?.plugin_url || '/'; // Get base URL from WP
        const pluginBaseUrl = pluginUrl.endsWith('/') ? pluginUrl : pluginUrl + '/';
        // This path should match where the build process copies the public assets
        baseUrl = `${pluginBaseUrl}dist/img/`;
      }
      // --- END: Updated Base URL Logic ---

      // Construct the final URL
      return `${baseUrl}${iconFilename}`;
    },
    parseCsvString(csvString) {
      if (!csvString || typeof csvString !== 'string') return [];
      try {
          const trimmedString = csvString.trim();
          if (trimmedString === '') return [];
          return trimmedString.split(',')
                                  .map(val => val.trim())
                                  .filter(val => val !== '')
                                  .map(val => parseFloat(val))
                                  .filter(val => !isNaN(val));
      } catch (error) {
          console.error(`>>> parseCsvString ERROR:`, error, `Input: ${csvString}`);
          return [];
      }
    },
    triggerChartReflow() {
        // Method kept for potential future use
        // ... (implementation as before) ...
    }
  } // End methods
}
</script>


<style lang="scss" scoped>

/* --- Your existing styles --- */

.erc__vehicle-remap-info {
    padding: 0 30px;
    // Inherit background and text color from parent container
    // background-color: var(--erc-bg-color); // Removed as requested
    color: var(--erc-text-color);
}

.section-image-info {
  width: 100%;
  min-height: 280px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  padding: 0 0 20px 0;
  align-items: center; /* Vertically align items */
  gap: 0px; /* Add gap between image and info */
}

.vehicle-image {
  img {
    width: 100% !important;
    height: auto; /* Maintain aspect ratio */
    display: block; /* Remove extra space below image */
    max-width: 500px; /* Optional: constrain max width */
    margin: 0 auto; /* Center if max-width is applied */
  }
}

.logo-and-basic-info {
  display: flex;
  text-align: left;
  column-gap: 20px;
  padding-top: 10px; /* Adjusted padding */
  justify-self: end;
}

.brand-logo {
  padding: 0; /* Simplified padding */
  flex-shrink: 0; /* Prevent logo from shrinking */
  img {
    width: 100%;
    max-width: 80px; /* Slightly smaller max-width */
    height: auto;
    display: block;
  }
}

.basic-info {
    flex-grow: 1; /* Allow basic info to take remaining space */
}

.brand-name {
  color: var(--erc-text-heading); // Use variable
  font-family: "Open Sans", sans-serif;
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 0.2em; /* Add small space */
}

.model-name {
  color: var(--erc-color-oem); // Use variable
  font-family: "Ubuntu", sans-serif;
  font-size: clamp(28px, 4vw, 40px); /* Responsive font size */
  font-weight: 900;
  line-height: 1.1; /* Adjusted line height */
  margin-bottom: 0.1em;
}

.generation-name,
.generation-year {
  color: var(--erc-text-light); // Use variable
  font-family: "Open Sans", sans-serif;
  font-size: clamp(18px, 2.5vw, 22px); /* Responsive font size */
  font-weight: 700;
  display: inline; /* Keep inline */
  line-height: 1.2;
}

.generation-year {
    margin-left: 0; /* Add space before year */
}


.engine-name {
  color: var(--erc-text-heading); // Use variable
  font-family: "Open Sans", sans-serif;
  font-size: clamp(18px, 2.5vw, 22px); /* Responsive font size */
  font-weight: 700;
  padding-bottom: 10px; /* Reduced padding */
  margin-top: 0.5em; /* Add space above engine name */
}

.engine-info-restructured {
  width: 100%;
  text-align: right;
  vertical-align: top;
  padding-top: 0px;
  line-height: 1.5; /* Adjust line height as needed */
}

.engine-detail-row {
  margin: 0 0 4px 0; /* Spacing between rows */
  font-weight: 300; /* MODIFIED: Set desired weight */
  font-size: 16px; /* Ensure font size is 16px */
  color: var(--erc-text-color); // Use variable
}

.engine-detail-label {
  margin: 8px 0 2px 0; /* Spacing for ECU label */
  font-weight: 700; /* Ensure label remains bold */
  font-size: 16px; /* Ensure font size is 16px */
  color: var(--erc-text-light); // Use variable
}


section.remaps_engine__titles {
  display: grid;
  grid-template-columns: 1fr auto; /* Adjust columns */
  color: var(--erc-text-heading); // Use variable
  font-family: "Open Sans", sans-serif;
  font-size: 16px;
  font-weight: 700;
  border-bottom: none;
  padding-bottom: 5px;
  margin-bottom: 15px;
}

.section-title.section-title__engine { /* Simplified selector */
  text-align: right;
}

.section-title {
  font-family: 'Ubuntu', sans-serif;
  font-size: 16px;
  font-weight: 700;
  padding: 0 0 15px 0; // Adjusted padding (removed top padding)
  margin: 0 0 15px 0; // Added margin-bottom
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--erc-text-heading); // Ensure color is set
  border-bottom: none; // Ensure no border from previous rules
}

/* Style titles within their new containers */
.remaps-data .section-title__remaps {
  text-align: left; // Keep left aligned on desktop
}

.remaps-info .section-title__engine {
    text-align: right;
    padding: 0;
    margin-bottom: 5px;
}


.tools,
.methods {
    margin-top: 15px;
    text-align: right;
    color: var(--erc-text-color); // Use variable
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;

  h5 { /* Keep H5 styles */
    font-family: 'Ubuntu', sans-serif;
    font-size: 15px;
    font-weight: 700 !important; /* Title is bold */
    padding: 0 0 5px 0;
    margin: 0 0 5px 0;
    color: var(--erc-text-heading); // Use variable
    text-transform: uppercase;
    border-bottom: none;
    display: inline-block;
    margin-bottom: 8px;
  }

  /* Apply font size to the list span */
  .tools-list,
  .methods-list { // Assuming you might have a methods-list class too
      font-size: 16px; /* Match engine info font size */
      font-weight: 300; /* Match engine info value weight */
      display: block; /* ADDED - Force list to new line */
      color: var(--erc-text-light); // Use variable
  }
}



section.remaps_engine__data {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 1fr); /* Flexible columns */
  gap: 30px; /* Add gap between remaps and engine info */
  padding: 0 0 40px 0;
  color: var(--erc-text-color); // Use variable
  margin-top: 0;

  .remaps-data {
    /* Takes first column */
  }

  .remaps-info {
    text-align: right;
    /* Takes second column */
  }
}

.available_remaps {
  display: grid;
  /* Adjust minmax to match the target box size */
  grid-template-columns: repeat(auto-fit, minmax(50px, max-content)); /* Use 100px min */
  gap: 15px;
}

.available-options {
  margin-top: 20px; /* Add some space above */
  padding-top: 15px; /* Add padding above */
  border-top: none;
}

.options-title {
  font-family: 'Ubuntu', sans-serif;
  font-size: 15px;
  font-weight: 700;
  margin: 0 0 8px 0; /* Space below title */
  color: var(--erc-text-heading); // Use variable
  text-transform: uppercase;
}

.options-list {
  font-size: 16px;
  color: var(--erc-text-light); // Use variable
  line-height: 1.5;
  margin: 0;
}

.column {
    min-width: 90px; /* Match the target box width/height */
}

.column-data {
    display: flex;
    flex-direction: column;
    height: 100%; /* Make column data fill height */
}

.heading {
  color: var(--erc-text-light); // Use variable
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  margin: 0 0 10px 0;
  text-align: center;
  line-height: 1.2;
  /* Prevent heading text from causing column to widen excessively */
  word-break: break-word;
}

/* Specific heading colors */
.heading__stage_1 { color: var(--erc-color-st1); }
.heading__stage_2 { color: var(--erc-color-st2); }
.heading__oem { color: var(--erc-color-oem); }


section.remaps_engine__data .data {
  display: grid;
  grid-template-columns: 1fr; /* Single column for data boxes */
  grid-row-gap: 10px;
  margin-top: auto; /* Push data boxes to the bottom */
}

.column .data-box {
  border: 2px solid var(--erc-color-oem); // Use variable
  border-radius: 8px;
  min-height: 100px; /* Ensure height matches donut */
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 8px;
  box-sizing: border-box;

  span.label {
    width: 100%;
    display: block;
    text-align: center;
    font-family: "Open Sans", sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: var(--erc-color-oem); // Use variable
    margin-bottom: 4px;
  }

  span.value {
    width: 100%;
    display: block;
    text-align: center;
    font-family: "Ubuntu", sans-serif;
    font-size: 25px; /* Increased font size */
    font-weight: 900;
    color: var(--erc-color-oem); // Use variable
    line-height: 1.1;
  }

  &.data-box__nm {
    background: var(--erc-color-oem); // Use variable
    color: #fff; // Assuming white text on colored background
    span.label, span.value {
      color: #ffffff;
    }
  }

  &.data-box__graph {
    border: none;
    padding: 0;
    background: none;
    min-height: auto; /* Let donut define height */
    height: auto; /* Let donut define height */

    .vc-donut {
        margin: 0 auto;
        display: block;
        width: 100px;
        height: 100px; /* This dictates the target height */
    }

    /* Styles for text within the donut */
    .cdc-text {
      span {
        font-weight: bold;
        font-family: "Open Sans", sans-serif;
      }

      /* Styles for absolute gain display */
      .absolute-gain-value {
        font-size: 20px;
        font-weight: bold;
        display: block;
        line-height: 1;
      }

      .absolute-gain-unit {
        font-size: 12px;
        font-weight: normal;
        display: block;
        line-height: 1;
        margin-top: 2px;
      }

      /* Styles for percentage display */
      span.percent-value {
        /* Match absolute-gain-value */
        font-size: 20px;
        display: block;
        line-height: 1;
      }

      span.percent-sign {
        /* Match absolute-gain-unit */
        font-size: 12px;
        font-weight: normal; /* Match unit weight */
        display: block;
        line-height: 1;
        margin-top: 2px; /* Match unit margin */
        margin-left: 0; /* Remove any previous left margin */
      }
    } /* Closing brace for .cdc-text */
  } /* Closing brace for &.data-box__graph */
} /* Closing brace for .column .data-box */


/* Stage 1 Specific Styles */
.column__stage_1 {
  .data-box:not(.data-box__graph) {
    border-color: var(--erc-color-st1); // Use variable
  }
  .data-box.data-box__nm {
    background: var(--erc-color-st1); // Use variable
    border-color: var(--erc-color-st1); // Use variable
    span.label, span.value { color: #ffffff; }
  }
  .data-box.data-box__hp {
    span.label { color: var(--erc-color-st1); } // Use variable
    span.value { color: var(--erc-color-st1); } // Use variable
  }
  .data-box__graph .cdc-text span { color: var(--erc-color-st1); } // Use variable
}

/* Stage 2 Specific Styles */
.column__stage_2 {
   .data-box:not(.data-box__graph) {
    border-color: var(--erc-color-st2); // Use variable
  }
  .data-box.data-box__nm {
    background: var(--erc-color-st2); // Use variable
    border-color: var(--erc-color-st2); // Use variable
    span.label, span.value { color: #ffffff; }
  }
  .data-box.data-box__hp {
    span.label { color: var(--erc-color-st2); } // Use variable
    span.value { color: var(--erc-color-st2); } // Use variable
  }
   .data-box__graph .cdc-text span { color: var(--erc-color-st2); } // Use variable
}

/* Modifications Tooltip */
.column__modifications {
    display: flex;
    align-items: flex-start;
    justify-content: left;
    justify-self: start !important;
    width: 50px !important;
    min-width: 50px !important;
    max-width: 50px !important;
    margin-left: 0;
    padding-top: 36px;
    z-index: 999;
}

.info-tooltip {
    &__popper {
        display: inline-block;
    }
  &__text {
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
    text-align: left;
    padding: 12px 15px;
    background: var(--erc-bg-color); // Use variable
    border-radius: 6px;
    width: 100%;
    min-width: 300px;
    font-size: 13px;
    line-height: 1.5;
    color: var(--erc-text-color) !important;  // Use variable
    z-index: 999 !important;

    h6, p { margin: 0; }
    h6 {
      margin-bottom: 8px;
      font-weight: bold;
      color: var(--erc-text-heading); // Use variable
      font-size: 14px;
    }
    p {
        margin-bottom: 5px;
        &:last-child { margin-bottom: 0; }
        strong { color: var(--erc-text-heading); } // Use variable
    }
  }

  &__reference {
    width: 28px;
    height: 28px;
    border: var(--erc-color-st2) solid 2px; // Use variable
    border-radius: 50%;
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: background-color 0.2s, color 0.2s;
    background-color: var(--erc-bg-color); // Use variable
    padding: 0;

    &:hover {
      background-color: var(--erc-color-st2); // Use variable
      span {
          background-color: transparent;
          color: #fff; // Assuming white text on hover
      }
    }

    span {
      display: inline-flex;
      justify-content: center;
      align-items: center;
      background-color: var(--erc-color-st2); // Use variable
      width: 20px;
      height: 20px;
      border-radius: 50%;
      color: #ffffff; // Assuming white text
      font-weight: bold;
      font-family: "Ubuntu", sans-serif;
      line-height: 1;
      font-size: 14px;
      transition: background-color 0.2s, color 0.2s;
    }
  }
}

.v-popper__wrapper {
    background-color: #fff !important;
    box-shadow: #ccc 0 0 6px !important;
    border-radius: 8px !important;
}



.info-tooltip__text { // This is a more specific selector for the tooltip content itself
      color: #000 !important; // This was likely an override for floating-vue's default.
                              // The .info-tooltip__text above already sets color: var(--erc-text-color)
                              // which is preferred for theming. Keeping this for now as it might
                              // address a specific floating-vue behavior.
      font-family: "Ubuntu", sans-serif !important;
      line-height: 1;
      font-size: 14px;
      font-weight: 300;
    }

    .v-popper__popper[data-popper-placement^=top] .v-popper__arrow-inner, .v-popper__popper[data-popper-placement^=top] .v-popper__arrow-outer {
    border-color: #fff !important;
    display: none !important;
}

div.v-popper__popper {
  background-color: #fff !important;
    color: #fff; // This color is for the popper container, text inside is handled by .info-tooltip__text
    border-radius: 10px !important;
}

.v-popper__popper.v-popper__popper--shown {
    visibility: visible;
    opacity: 1;
    background: #fff;
    border-radius: 10px;
    box-shadow: #888 0px 0px 12px;
}

.v-popper__popper[data-popper-placement^=top] .v-popper__arrow-inner, .v-popper__popper[data-popper-placement^=top] .v-popper__arrow-outer {
    border-top-color: #fff !important;
}



/* Styles for Method Icons */
.methods-icon-container {
  /* Allows icons to wrap if needed */
  display: flex;
  flex-wrap: wrap;
  /* Align icons to the right since the parent (.methods) is text-align: right */
  justify-content: flex-end;
  gap: 8px; /* Spacing between icons */
  margin-top: 5px; /* Space below the Methods title */
}

.method-icon {
  display: inline-block; /* Or block if flex container handles layout */
  width: 60px;
  height: 60px;
  margin-top: -20px;
  /* Optional: Add border, background, etc. if needed */
  /* border: 1px solid var(--erc-border-color); */
  /* object-fit: contain; /* Adjust how SVG scales if needed */
}

/* --- NEW: Styles for Dynochart Section --- */
.dynochart-section {
    margin-top: 0px; /* Space above the chart section */
    padding-top: 20px;
    border-top: none;
}

.section-title__dynochart {
    /* Inherits from .section-title, add specifics if needed */
    text-align: left;
    margin-bottom: 15px;
     font-family: 'Ubuntu', sans-serif;
    font-size: 16px;
    font-weight: 700;
    padding: 1rem 0 0.2rem 0;
    margin: 0 0 15px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--erc-text-heading); // Use variable
}

/* Highcharts will adapt to this container */
.chart-container {
    width: 100%;
    height: 450px;
}

h6.options-title {
    text-align: left;
}


/* --- Donut Chart Center Background Theming --- */
/* Uses --erc-donut-center-bg defined in VehicleCatalog.vue */
:deep(.vc-donut .cdc-overlay),
:deep(.vc-donut .vc-donut-content) {
  background-color: var(--erc-donut-center-bg) !important;
}

/* Optional: If the text color inside also needs adjustment for dark theme */
/*
:deep(.erc-vehicle-catalog-container.theme-dark .vc-donut .cdc-text) {
  color: var(--erc-text-heading) !important; // Example: Use heading color
}
*/




/* Responsive Adjustments */


@media only screen and (max-width: 1024px) {
    section.remaps_engine__data {
        grid-template-columns: 1fr; /* Stack columns */
        gap: 40px;
    }
    .remaps-info, .engine-info-restructured, .options, .methods, .tools { // Updated selector
        text-align: left; /* Align text left when stacked */
    }
    .remaps-info .section-title__engine {
    text-align: left;
    padding: 0;
    margin-bottom: 5px;
}

     /* Adjust titles alignment for stacked view */
     .remaps-info .section-title__engine,
    .remaps-data .section-title__remaps {
        text-align: center; // Align both left when stacked
    }

    .tools,
    .methods {
        text-align: center; /* Ensure alignment is correct when stacked */
    }
    .engine-info-restructured {
        text-align: center; /* Align left when stacked */
    }
    .available_remaps {
         /* Adjust minmax if needed, maybe revert to 90px? */
         grid-template-columns: repeat(auto-fit, minmax(90px, 1fr));
         gap: 10px;
    }
     .column {
        min-width: 90px; /* Revert min-width */
     }
     .column .data-box {
         min-height: 90px; /* Revert height */
         span.value { font-size: 22px; } /* Revert font size */
     }
     /* Donut size remains 100px unless changed here */

    .column__modifications {
        margin-left: 0;
        justify-content: flex-start;
        max-width: none;
        margin-top: 10px;
        z-index: 999 !important;
    }
     .info-tooltip__reference {
         margin: 0;
     }
     .section-title__dynochart {
        text-align: center; /* Center title on smaller screens */
    }

    .chart-container {
    width: 100%;
    height: 350px;
}

}


@media only screen and (max-width: 740px) {
  .erc__vehicle-remap-info {
      padding: 0;
  }
  .section-image-info {
    padding: 0px 0 0 0;
    grid-template-columns: 1fr; /* Stack image and info */
    text-align: center;
  }
  .vehicle-image {
    order: 2;
}
  .vehicle-image img {
    max-width: 100%;
    margin-bottom: 0px;
    padding-top: 0px;
  }

  .logo-and-basic-info {
        text-align: center;
        flex-direction: row;
        align-items: start;
        gap: 10px;
        width: 100%;
        padding-bottom: 30px;
    }
  .brand-logo {
    padding: 0;
        z-index: 999;
        background: #fff;
        left: 10px;
    img { max-width: 110px; border: 1px solid #d0d0d0; padding: 10px; }
  }

  .basic-info {
      text-align: left;
  }

  .engine-name {
    font-size: 18px;
    padding-bottom: 10px;
    margin-top: 2px;
}


    .available_remaps {
         /* Adjust minmax if needed, maybe 80px? */
         grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    }
     .column {
        min-width: 80px; /* Adjust min-width */
     }
    .column .data-box {
        min-height: 100px; /* Adjust height */
        span.value { font-size: 32px; } /* Adjust font size */
        span.label { font-size: 14px; }
    }
     /* Adjust donut size if needed */
     /* .column .data-box.data-box__graph .vc-donut { width: 80px; height: 80px; } */
     .data-box__graph .cdc-text .percent-value,
     .data-box__graph .cdc-text .absolute-gain-value { font-size: 18px; } // Adjusted selector
     .data-box__graph .cdc-text .percent-sign,
     .data-box__graph .cdc-text .absolute-gain-unit { font-size: 12px; } // Adjusted selector

     .methods-icon-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
    margin-top: 5px;
  }

  h6.options-title {
    text-align: center;
}

.options-list {
    text-align: center;
}

.chart-container {
    width: 100%;
    height: 350px;
}

.field-group label {
    display: none;
}

.chart-container {
    width: 100%;
    height: 200px;
}

}

@media only screen and (max-width: 480px) {
    .available_remaps {
         grid-template-columns: repeat(2, 1fr); /* Force 2 columns */
         gap: 15px;
    }
     /* Column min-width might be less relevant here */
     .column__modifications {
         justify-content: center;
     }
     /* Heights/fonts inherit from 740px breakpoint unless overridden */
}

</style>

